import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { readPage, readItem } from '../../../utils/apiResult';

// Task 23/35 — IDP yeni yapı: DevelopmentPlan → DevelopmentGoal → DevelopmentAction.
// Eski Faz A tek-katmanlı actions[] yapısı (actionType: training|coaching|shadowing|
// reading) KALKTI — bu yüzden eski FE'nin gönderdiği düz `actions[]` DTO'su backend'in
// yeni `goals[].actions[]` sözleşmesiyle uyuşmuyordu (sessiz-kırık: hedefsiz taslak
// üretiyordu, bkz. progress.md T23 KOORDİNASYON notu). Bu dosya YENİ sözleşmeye göre
// yeniden yazıldı.

export const GOAL_CATEGORIES = ['technical', 'leadership', 'personal'];
// P1 — coaching|certification eklendi (BE ValidActionTypes ile birebir; eski
// 4 tür aynen korunur).
export const ACTION_TYPES = ['course', 'mentoring', 'project', 'rotation', 'coaching', 'certification'];
export const ACTION_STATUSES = ['planned', 'in_progress', 'completed', 'cancelled'];

// Akış: draft → meeting → manager_approval → (revision_required ⇄) → active →
// period_end_review → completed. "cancelled" akış dışı (her durumdan ulaşılabilir).
export const IDP_STATUSES = [
  'draft', 'meeting', 'manager_approval', 'revision_required',
  'active', 'period_end_review', 'completed', 'cancelled',
];

// Akış çubuğu — 6 ana adım (revision_required kendi adımı değil, manager_approval
// üzerinde bir rozet/dal olarak gösterilir; cancelled akış dışı, ayrı rozet).
export const IDP_FLOW_STEPS = [
  'draft', 'meeting', 'manager_approval', 'active', 'period_end_review', 'completed',
];

const useMyDevelopmentPlanApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  // TD-23 — bkz. utils/apiResult.js: GET okumaları reject etmez, sinyal res.data.status.
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`/v1/development-plans/my?${new URLSearchParams(params)}`)
      .then((res) => {
        const { ok, items, pagination: page } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
        setPagination(page);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (payload) => {
    try {
      await api.post('/v1/development-plans/my', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const update = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/development-plans/my/${id}`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // TD-23 — başarısız okumada açıkça null (bkz. utils/apiResult.js).
  const getById = useCallback(async (id) => {
    const res = await api.get(`/v1/development-plans/my/${id}`);
    return readItem(res);
  }, []);

  const submit = useCallback(async (id) => {
    try {
      await api.post(`/v1/development-plans/my/${id}/submit`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const cancel = useCallback(async (id) => {
    try {
      await api.post(`/v1/development-plans/my/${id}/cancel`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const updateActionProgress = useCallback(async (planId, actionId, payload) => {
    try {
      await api.post(`/v1/development-plans/my/${planId}/actions/${actionId}/progress`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'goals', label: ' ', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'overallProgress', label: t('performance.idp.overallProgress'), options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'readyForReview', label: ' ', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'title',
      label: t('performance.idp.title'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'overallProgressDisplay',
      label: t('performance.idp.overallProgress'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const v = data[tableMeta.rowIndex]?.overallProgress;
          return v != null ? `${v}%` : '-';
        },
      },
    },
    {
      name: 'statusDisplay',
      label: t('performance.idp.fields.status'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row ? t(`performance.idp.status.${row.status}`, { defaultValue: row.status }) : '-';
        },
        filterOptions: {
          // Backend `GetMy` yalnız `status[in]` okur (StatusIn, CSV) — `status[eq]`
          // sessizce yoksayılırdı (Faz A'dan miras no-op filtre; bkz. T31 ledger notu
          // "name[like] no-op" bulgusuyla aynı sınıf). Burada gerçek sözleşmeye
          // (status[in]) uyacak şekilde düzeltildi.
          defaultOp: 'in',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('performance.idp.fields.status')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => {
                  fl[i] = e.target.value ? [e.target.value] : [];
                  onChange(fl[i], i, col);
                }}
              >
                <MenuItem value=""><em>-</em></MenuItem>
                {IDP_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{t(`performance.idp.status.${s}`, { defaultValue: s })}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    dateFilterColumn('createDate', t('performance.common.createDate'), lang),
  ], [t, lang, data]);

  return {
    fetching,
    data,
    pagination,
    loadFailed,
    columns,
    create,
    update,
    getById,
    submit,
    cancel,
    updateActionProgress,
    refresh,
  };
};

export default useMyDevelopmentPlanApi;
